import React, {
  memo,
  PropsWithChildren,
  useEffect,
  useMemo,
} from 'react';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import {
  AxiosError,
  AxiosResponse,
} from 'axios';
import isNil from 'lodash/isNil';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import {
  getErrorMessage,
  isNoAccessError,
} from 'utils/errors';
import {
  useAuth,
  useNotify,
} from 'core/hooks';
import {
  useImpersonation,
} from 'core/context/impersonation';
import {
  browserId,
  tabId,
} from 'utils/correlationIds';
import {
  axiosInstance,
} from 'utils/httpClient';
import isEmpty from 'lodash/isEmpty';
import {
  datadogLogs,
} from '@datadog/browser-logs';

// 403/404 mean the record isn't available to this user, an expected outcome, so they log as warnings.
export function logRequestFailure(error: AxiosError) {
  datadogLogs.logger.log(
    `API request failed ${error?.config?.url ?? ''}`,
    {
      errorDetails: {
        method: error?.config?.method,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        code: error?.code,
        request_id: error?.config?.headers?.['X-Client-Request-ID'],
        responseData: typeof error?.response?.data === 'object' ? JSON.stringify(error?.response?.data) : error?.response?.data,
      },
    },
    isNoAccessError(error) ? 'warn' : 'error',
  );
}

function ReactQueryProvider({ children }: PropsWithChildren) {
  const {
    logout,
    issueAccessToken,
    isAuthenticated,
  } = useAuth();
  const notify = useNotify();
  const {
    updateImpersonation,
    stopImpersonation,
    maybeAttachSavedImpersonationHeader,
    findImpersonationHeader,
  } = useImpersonation();
  const onError = (error: unknown) => {
    const message = getErrorMessage(error);

    if (!isEmptyString(message) && !isEmpty(message)) {
      notify({
        variant: 'error',
        message,
      });
    }
  };

  const retryFn = (failureCount: number, error: unknown) => {
    const { status } = (error as AxiosError)?.response ?? {};
    if (isAuthenticated && failureCount < 2 && status === 401) {
      issueAccessToken();
      return true;
    }

    // failure count is over 3
    if (status === 401) {
      console.warn('[Auto Logout] Received repeated 401 responses.', error);
      logout();
    }

    return false;
  };

  const commonOptions = useMemo(() => ({
    retry: retryFn,
    onError,
  }), [retryFn, onError]);

  const queryClient = useMemo(() => new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 10 * 60 * 1000,
        gcTime: 0,
        ...commonOptions,
      },
      mutations: commonOptions,
    },
  }), [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      const requestInterceptor = axiosInstance.interceptors.request.use(
        async (config) => {
          const token = await issueAccessToken();
          const headers = config.headers as Record<string, string>;

          if (notEmptyString(token)) {
            headers.Authorization = `Bearer ${token}`;
          }

          maybeAttachSavedImpersonationHeader(headers);

          headers['X-Client-Browser-Id'] = browserId;
          headers['X-Client-Tab-ID'] = tabId;
          headers['X-Client-Request-ID'] = crypto.randomUUID();

          return config;
        },
      );

      const responseInterceptor = axiosInstance.interceptors.response.use(
        async (response: AxiosResponse) => {
          const impersonationHeader = findImpersonationHeader(
            response.headers as Record<string, string>,
          );

          if (!isNil(impersonationHeader)) {
            updateImpersonation(impersonationHeader);
          }

          return response;
        },
        (error: AxiosError) => {
          logRequestFailure(error);

          if (!isNil(error?.response?.headers?.['x-impersonation-terminated'])) {
            stopImpersonation();
          }
          return Promise.reject(error);
        },
      );

      return () => {
        axiosInstance.interceptors.request.eject(requestInterceptor);
        axiosInstance.interceptors.response.eject(responseInterceptor);
      };
    }

    return undefined;
  }, [isAuthenticated, updateImpersonation, stopImpersonation]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* <ReactQueryDevtools position="left" /> */}
    </QueryClientProvider>
  );
}

export default memo(ReactQueryProvider);
